import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Radio, Loader2, MapPin, Play } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLiveStreams = () => {
  const { token } = useAuth();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    title: '',
    gurudwara_name: '',
    youtube_id: '',
    description: '',
    location: '',
    is_live: true
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStreams = async () => {
    try {
      const response = await axios.get(`${API_URL}/live-streams`);
      setStreams(response.data);
    } catch (error) {
      toast.error('Failed to fetch streams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStreams();
  }, []);

  const resetForm = () => {
    setForm({ title: '', gurudwara_name: '', youtube_id: '', description: '', location: '', is_live: true });
  };

  const extractYouTubeId = (url) => {
    if (!url) return url;
    const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    return match ? match[1] : url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      ...form,
      youtube_id: extractYouTubeId(form.youtube_id)
    };

    try {
      await axios.post(`${API_URL}/admin/live-streams`, payload, { headers });
      toast.success('Live stream added successfully');
      setDialogOpen(false);
      resetForm();
      fetchStreams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add stream');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (streamId) => {
    if (!window.confirm('Are you sure you want to remove this stream?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/live-streams/${streamId}`, { headers });
      toast.success('Stream removed');
      fetchStreams();
    } catch (error) {
      toast.error('Failed to remove stream');
    }
  };

  return (
    <div data-testid="admin-live-streams">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">Live Streams</h1>
          <p className="text-gray-600">Manage Gurudwara live kirtan streams</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-stream-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Stream
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Add Live Stream</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="gurudwara_name">Gurudwara Name *</Label>
                <Input
                  id="gurudwara_name"
                  value={form.gurudwara_name}
                  onChange={(e) => setForm({ ...form, gurudwara_name: e.target.value })}
                  placeholder="e.g., Sri Harmandir Sahib"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="title">Stream Title *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Golden Temple Live Kirtan"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="youtube_id">YouTube Video ID or URL *</Label>
                <Input
                  id="youtube_id"
                  value={form.youtube_id}
                  onChange={(e) => setForm({ ...form, youtube_id: e.target.value })}
                  placeholder="e.g., dQw4w9WgXcQ or full YouTube URL"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Enter the video ID or paste the full YouTube URL</p>
              </div>
              
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="City, Country"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description..."
                />
              </div>
              
              <div className="flex items-center gap-3 py-2">
                <Switch
                  id="is_live"
                  checked={form.is_live}
                  onCheckedChange={(checked) => setForm({ ...form, is_live: checked })}
                />
                <Label htmlFor="is_live">Currently Live</Label>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 btn-primary" disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Add Stream
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-saffron-500" />
        </div>
      ) : streams.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Radio className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No live streams yet</h3>
            <p className="text-gray-500">Add Gurudwara live streams</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {streams.map((stream) => (
            <Card key={stream.id} className="overflow-hidden hover:shadow-md transition-shadow">
              <div className="aspect-video relative">
                <img
                  src={`https://img.youtube.com/vi/${stream.youtube_id}/mqdefault.jpg`}
                  alt={stream.title}
                  className="w-full h-full object-cover"
                />
                {stream.is_live && (
                  <Badge className="absolute top-2 left-2 bg-red-600 text-white">LIVE</Badge>
                )}
                <a
                  href={`https://youtube.com/watch?v=${stream.youtube_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity"
                >
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <Play className="w-6 h-6 text-gray-900 ml-1" />
                  </div>
                </a>
              </div>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900">{stream.gurudwara_name}</h3>
                    <p className="text-sm text-gray-500">{stream.title}</p>
                    {stream.location && (
                      <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" /> {stream.location}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="text-red-500 hover:bg-red-50"
                    onClick={() => handleDelete(stream.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminLiveStreams;
